import { Link } from '@tanstack/react-router';
import { Barcode, Plus, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { EmptyState } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import type { SkuRule } from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent } from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { SkuRuleCreate, SkuRuleEdit } from '../components/sku-rule-editor';
import { useSkuRules } from '../hooks/use-sku-rules';

/** The counter(s) of a rule, in words: one number, or one per type. */
function NextNumbers({ rule }: { rule: SkuRule }) {
  const { t } = useTranslation('skuRules');
  if (rule.rootTemplate === null) {
    return <span className="text-muted-foreground text-xs">{t('list.manualRoot')}</span>;
  }
  if (rule.counters.length === 1 && rule.counters[0]?.scopeKey === '') {
    return <span className="tabular-nums">{rule.counters[0].nextNumber}</span>;
  }
  return (
    <ul className="flex flex-col text-xs">
      {rule.counters.map((counter) => (
        <li key={counter.scopeKey} className="tabular-nums">
          {t('list.counterFor', {
            name:
              rule.productTypes.find((type) => type.id === counter.scopeKey)?.name ??
              counter.scopeKey,
            number: counter.nextNumber,
          })}
        </li>
      ))}
    </ul>
  );
}

/** Body of the SKU rules tab; the catalog setup layout supplies the page header. */
function SkuRules() {
  const { t } = useTranslation('skuRules');
  const query = useSkuRules();
  const rows = query.data;

  const newButton = (
    <Can permission="attributes.write">
      <Button asChild>
        <Link to="/org/catalog-setup/sku-rules/new">
          <Plus aria-hidden />
          {t('new')}
        </Link>
      </Button>
    </Can>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <p className="text-muted-foreground max-w-prose text-sm">{t('description')}</p>
        {newButton}
      </div>
      {query.error ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : !rows ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-12" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Barcode}
          title={t('empty.title')}
          description={t('empty.description')}
          action={newButton}
        />
      ) : (
        <Card className="py-0">
          <CardContent className="px-0">
            <Table aria-label={t('title')}>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-6">{t('columns.rule')}</TableHead>
                  <TableHead>{t('columns.productTypes')}</TableHead>
                  <TableHead>{t('columns.samples')}</TableHead>
                  <TableHead className="text-right">{t('columns.nextNumber')}</TableHead>
                  <TableHead className="pr-6 text-right">{t('columns.products')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((rule) => (
                  <TableRow key={rule.id} aria-label={rule.name}>
                    <TableCell className="pl-6 align-top">
                      <div className="flex flex-wrap items-center gap-2">
                        <Link
                          to="/org/catalog-setup/sku-rules/$ruleId"
                          params={{ ruleId: rule.id }}
                          className="font-medium hover:underline"
                        >
                          {rule.name}
                        </Link>
                        {rule.isDefault ? (
                          <Badge variant="secondary" className="font-normal">
                            {t('list.default')}
                          </Badge>
                        ) : null}
                        {rule.warnings.length > 0 ? (
                          <Badge
                            variant="outline"
                            className="border-warning/60 bg-warning/15 font-normal"
                            title={rule.warnings.map((w) => w.message).join('\n')}
                          >
                            <TriangleAlert aria-hidden />
                            {t('list.warnings', { count: rule.warnings.length })}
                          </Badge>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="align-top">
                      {rule.productTypes.length === 0 ? (
                        <span className="text-muted-foreground text-xs">
                          {rule.isDefault ? t('list.unclaimedTypes') : t('list.noTypes')}
                        </span>
                      ) : (
                        <ul className="flex flex-wrap gap-1" aria-label={t('columns.productTypes')}>
                          {rule.productTypes.map((type) => (
                            <li key={type.id}>
                              <Badge variant="outline" className="font-normal">
                                {type.name}
                              </Badge>
                            </li>
                          ))}
                        </ul>
                      )}
                    </TableCell>
                    <TableCell className="align-top">
                      <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-xs">
                        <dt className="text-muted-foreground">{t('list.rootSample')}</dt>
                        <dd>
                          {rule.rootTemplate === null ? (
                            <span className="text-muted-foreground">{t('list.manualRoot')}</span>
                          ) : (
                            <code>{rule.rootTemplate}</code>
                          )}
                        </dd>
                        <dt className="text-muted-foreground">{t('list.variantSample')}</dt>
                        <dd>
                          <code>{rule.variantTemplate}</code>
                        </dd>
                      </dl>
                    </TableCell>
                    <TableCell className="text-right align-top text-sm">
                      <NextNumbers rule={rule} />
                    </TableCell>
                    <TableCell className="pr-6 text-right align-top text-sm tabular-nums">
                      {rule.productCount}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/**
 * The feature door exports only this page, so the tab's list, new and detail routes all render it
 * and pick the view; `edit` takes the id from the route's params.
 */
export type SkuRulesPageProps =
  { view?: 'list' } | { view: 'new' } | { view: 'edit'; ruleId: string };

export function SkuRulesPage(props: SkuRulesPageProps) {
  return (
    <FeatureErrorBoundary name="dictionary.skuRules">
      {props.view === 'new' ? (
        <SkuRuleCreate />
      ) : props.view === 'edit' ? (
        <SkuRuleEdit ruleId={props.ruleId} />
      ) : (
        <SkuRules />
      )}
    </FeatureErrorBoundary>
  );
}
